import React from 'react';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import YearEndFormsService from 'src/services/year-end-forms.service';
import { useFormik } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import Arrow from 'src/assets/arrow-left-black.svg';
import { CommonCurrencyInput, RegularButton, CommonInput, CustomCheckBox, AlertAdapter } from 'pg-ui-library';
import { ArrowBack, Container, NavTextLink, PageTitle, Row, Rowlast, RowItem, Subtitle, RowFirst } from './styles';

export const FormTableEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialData = {
    w2Form: {
      year: '',
      ssn: '',
      controlNumber: '',
      middleName: null,
      firstName: '',
      lastName: '',
      city: '',
      state: '',
      zipCode: '',
      zipCode4: null,
      federalWages: 0,
      socialSecurityWages: 0,
      medicareWages: 0,
      socialSecurityTips: 0,
      federalTaxWithHeld: 0,
      socialSecurityTaxWithHeld: 0,
      medicareTaxWithHeld: 0,
      allocatedTips: 0,
      dependentCareBenefits: 0,
      nonQualifiedPlan457: 0,
      statutoryEmployeeIndicator: false,
      retirementPlanIndicator: true,
      thirdPartySickPayIndicator: false,
      corrected: false,
    },
    company: {
      identificationNumber: '',
      name: '',
      fullPrimaryAddress: '',
      zipCode: '',
    },
    w2FormTaxes: [
      {
        state1: '',
        state2: '',
        stateWages1: 0,
        stateIncomeTax1: 0,
        formLocal: {
          localWages1: null,
          localIncomeTax1: 0,
          localityName1: '',
        },
      },
      {
        state1: '',
        state2: '',
        stateWages1: 0,
        stateIncomeTax1: 0,
        formLocal: {
          localWages1: 0,
          localIncomeTax1: 0,
          localityName1: '',
        },
      },
    ],
    w2FormSection12: {
      section12Code1: 'DD',
      section12Amount1: null,
      section12Code2: null,
      section12Amount2: null,
      section12Code3: null,
      section12Amount3: null,
      section12Code4: null,
      section12Amount4: null,
    },
    w2FormSection14: {
      section14Label1: null,
      section14Amount1: null,
      section14Label2: null,
      section14Amount2: null,
      section14Label3: null,
      section14Amount3: null,
      section14Label4: null,
      section14Amount4: null,
      section14Label5: null,
      section14Amount5: null,
    },
  };

  interface W2Form {
    year: string;
    ssn: string;
    controlNumber: string;
    middleName: string;
    firstName: string;
    lastName: string;
    city: string;
    state: string;
    zipCode: string;
    zipCode4: string;
    federalWages: number | null;
    socialSecurityWages: number | null;
    medicareWages: number | null;
    socialSecurityTips: number | null;
    federalTaxWithHeld: number | null;
    socialSecurityTaxWithHeld: number | null;
    medicareTaxWithHeld: number | null;
    allocatedTips: number | null;
    dependentCareBenefits: number | null;
    nonQualifiedPlan457: number | null;
    statutoryEmployeeIndicator: false;
    retirementPlanIndicator: true;
    thirdPartySickPayIndicator: false;
    corrected: false;
  }
  interface Company {
    identificationNumber: string;
    name: string;
    fullPrimaryAddress: string;
    zipCode: string;
  }
  interface W2FormTax {
    state1: string;
    state2: string;
    stateWages1: number | null;
    stateIncomeTax1: number | null;
    formLocal: {
      localWages1: number | null;
      localIncomeTax1: number | null;
      localityName1: string;
    };
  }
  interface W2FormSection12 {
    section12Amount1: number | null;
    section12Code2: string;
    section12Amount2: number | null;
    section12Code3: string;
    section12Amount3: number | null;
    section12Code4: string;
    section12Amount4: number | null;
  }
  interface W2FormSection14 {
    section14Label1: string;
    section14Amount1: number | null;
    section14Label2: string;
    section14Amount2: number | null;
    section14Label3: string;
    section14Amount3: number | null;
    section14Label4: string;
    section14Amount4: number | null;
    section14Label5: string;
    section14Amount5: number | null;
  }
  interface InitialData {
    w2Form: W2Form;
    company: Company;
    w2FormTaxes: W2FormTax[];
    w2FormSection12: W2FormSection12;
    w2FormSection14: W2FormSection14;
  }
  const { year } = useParams();
  const { formType } = useParams();
  const { employeeId } = useParams();
  const { controlNumber } = useParams();
  const { data: dataCompanies } = useTypedSelector(state => state.config);
  const { data: employeeData, isLoading } = useQuery(['employeeDetail'], () =>
    YearEndFormsService.getEmployeeDetailYearEndForm(
      dataCompanies.config?.selectedCompanyId as unknown as '',
      year || '',
      employeeId || '',
      controlNumber || ''
    )
  );

  const mutation = useMutation(
    (data: InitialData) =>
      YearEndFormsService.editEmployeeDetailYearEndForm(dataCompanies.config?.selectedCompanyId as unknown as '', data),
    {
      onSuccess: () => {
        AlertAdapter('Employee Form updated successfully', 'success');
        navigate(`/year-end-forms/${year}/${formType}`, { replace: true });
      },
      onError: (e: Error) => {
        AlertAdapter(e.message, 'error');
      },
    }
  );

  const initialValues = employeeData?.data ? { ...employeeData.data } : { ...initialData };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values: any) => {
      try {
        await mutation.mutate(values);
      } catch (error) {
        // no redirect
      }
    },
  });

  return (
    <Container>
      {!isLoading && (
        <>
          <Row style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <NavTextLink to={`/year-end-forms/${year}/${formType}`}>
                <ArrowBack src={Arrow} />
              </NavTextLink>
              <PageTitle>{t('w2.form.edit')}</PageTitle>
            </div>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <div style={{ marginTop: '15px' }}>
                <CustomCheckBox
                  label={t('global.form.saveAsCorrected')}
                  id="saveAsCorrected"
                  name="saveAsCorrected"
                  checked={formik.values.w2Form.corrected}
                  onChange={() => {
                    formik.setFieldValue('w2Form.corrected', !formik.values.w2Form.corrected);
                  }}
                />
              </div>
              <RegularButton
                onClick={() => formik.handleSubmit()}
                type="submit"
                disabled={isLoading || mutation.isLoading}
              >
                {t('global.form.save')}
              </RegularButton>
            </div>
          </Row>
          <Subtitle style={{ margin: 0 }}>{t('w2.form.value.Configuration')}</Subtitle>
          <RowFirst>
            <RowItem>
              <CommonInput
                id="firstName"
                name="firstName"
                label={t('personalInfo.generalInfo.firstName')}
                placeholder="Last Name"
                value={formik.values.w2Form.firstName || ''}
                onChange={event => formik.setFieldValue('w2Form.firstName', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonInput
                id="middleName"
                name="middleName"
                label={t('personalInfo.generalInfo.middleName')}
                placeholder="Last Name"
                value={formik.values.w2Form.middleName || ''}
                onChange={event => formik.setFieldValue('w2Form.middleName', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonInput
                id="lastName"
                name="lastName"
                label={t('personalInfo.generalInfo.lastName')}
                placeholder="Last Name"
                value={formik.values.w2Form.lastName || ''}
                onChange={event => formik.setFieldValue('w2Form.lastName', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonInput
                id="address1"
                name="address1"
                label={t('personalInfo.workLocation.address')}
                placeholder="Address"
                value={formik.values.w2Form.address1 || ''}
                onChange={event => formik.setFieldValue('w2Form.address1', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonInput
                id="city"
                name="city"
                label="City"
                placeholder="City"
                value={formik.values.w2Form.city || ''}
                onChange={event => formik.setFieldValue('w2Form.city', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonInput
                id="state"
                name="state"
                label="State"
                placeholder="State"
                value={formik.values.w2Form.state || ''}
                onChange={event => formik.setFieldValue('w2Form.state', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonInput
                id="zipcode"
                name="zipcode"
                label={t('personalInfo.address.zipcode')}
                placeholder="Zip Code"
                value={formik.values.w2Form.zipCode || ''}
                onChange={event => formik.setFieldValue('w2Form.zipCode', event.target.value)}
              />
            </RowItem>
          </RowFirst>
          <Subtitle style={{ margin: 0 }}>{t('w2.form.value.Configuration')}</Subtitle>
          <Row>
            <RowItem>
              <CommonCurrencyInput
                style={{ maxWidth: '23rem' }}
                id="federalWages"
                label={`1. ${t('w2.form.value.wageTips')}`}
                name="federalWages"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.federalWages}
                onValueChange={v => {
                  if (Number.isNaN(v)) {
                    formik.setFieldValue('w2Form.federalWages', 0);
                  } else {
                    formik.setFieldValue('w2Form.federalWages', v);
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                style={{ maxWidth: '23rem' }}
                id="federalTaxWithHeld"
                label={`2. ${t('w2.form.value.FedralIncomeTax')}`}
                name="federalTaxWithHeld"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.federalTaxWithHeld}
                onValueChange={v => {
                  if (Number.isNaN(v)) {
                    formik.setFieldValue('w2Form.federalTaxWithHeld', 0);
                  } else {
                    formik.setFieldValue('w2Form.federalTaxWithHeld', v);
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                style={{ maxWidth: '23rem' }}
                id="socialSecurityWages"
                label={`3. ${t('w2.form.value.ssw')}`}
                name="socialSecurityWages"
                placeholder="Please enter a number"
                prefix="$"
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.socialSecurityWages}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.socialSecurityWages', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.socialSecurityWages', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.socialSecurityWages', 0);
                    }
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                style={{ maxWidth: '23rem' }}
                id="sswWithheld"
                label={`4. ${t('w2.form.value.sswWithheld')}`}
                name="sswWithheld"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.socialSecurityTaxWithHeld}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.socialSecurityTaxWithHeld', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.socialSecurityTaxWithHeld', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.socialSecurityTaxWithHeld', 0);
                    }
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                style={{ maxWidth: '23rem' }}
                id="MedicalWageTips"
                label={`5. ${t('w2.form.value.MedicalWageTips')}`}
                name="MedicalWageTips"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.medicareWages}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.medicareWages', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.medicareWages', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.medicareWages', 0);
                    }
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                style={{ maxWidth: '23rem' }}
                id="MedicalWageTips"
                label={`6. ${t('w2.form.value.MedicalTaxesWithheld')}`}
                name="MedicalWageTips"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.medicareTaxWithHeld}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.medicareTaxWithHeld', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.medicareTaxWithHeld', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.medicareTaxWithHeld', 0);
                    }
                  }
                }}
              />
            </RowItem>
          </Row>
          <Row>
            <RowItem>
              <CommonCurrencyInput
                id="SocialSecurityTips"
                label={`7. ${t('w2.form.value.SocialSecurityTips')}`}
                name="SocialSecurityTips"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.socialSecurityTips}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.socialSecurityTips', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.socialSecurityTips', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.socialSecurityTips', 0);
                    }
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="AllocatedTips"
                label={`8. ${t('w2.form.value.AllocatedTips')}`}
                name="AllocatedTips"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.allocatedTips}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.allocatedTips', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.allocatedTips', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.allocatedTips', 0);
                    }
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="DependentCareBenefit"
                label={`10. ${t('w2.form.value.DependentCareBenefit')}`}
                name="DependentCareBenefit"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.dependentCareBenefits}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.dependentCareBenefits', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.dependentCareBenefits', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.dependentCareBenefits', 0);
                    }
                  }
                }}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="NonQualifiledPlans"
                label={`11. ${t('w2.form.value.NonQualifiledPlans')}`}
                name="NonQualifiledPlans"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2Form.nonQualifiedPlan457}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2Form.nonQualifiedPlan457', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2Form.nonQualifiedPlan457', numericValue);
                    } else {
                      formik.setFieldValue('w2Form.nonQualifiedPlan457', 0);
                    }
                  }
                }}
              />
            </RowItem>
            <RowItem>{/* Another empty placeholder for the sixth item */}</RowItem>
          </Row>
          <Row>
            <RowItem>
              <CommonCurrencyInput
                id="section12Amount1"
                label={`12. ${t('w2.form.value.SeeABox')}`}
                name="section12Amount1"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection12.section12Amount1}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection12.section12Amount1', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection12.section12Amount1', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection12.section12Amount1', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section12Amount4"
                name="section12Amount4"
                label=""
                value={formik.values.w2FormSection12.section12Code1}
                onChange={event => formik.setFieldValue('w2FormSection12.section12Code1', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="section12Amount2"
                label="12 b."
                name="section12Amount2"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection12.section12Amount2}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection12.section12Amount2', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection12.section12Amount2', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection12.section12Amount2', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section12Code2"
                name="section12Code2"
                label=""
                value={formik.values.w2FormSection12.section12Code2}
                onChange={event => formik.setFieldValue('w2FormSection12.section12Code2', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="section12Amount3"
                label="12 c."
                name="section12Amount3"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection12.section12Amount3}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection12.section12Amount3', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection12.section12Amount3', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection12.section12Amount3', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section12Code3"
                name="section12Code3"
                label=""
                value={formik.values.w2FormSection12.section12Code3}
                onChange={event => formik.setFieldValue('w2FormSection12.section12Code3', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="section12Amount4"
                label="12 d."
                name="section12Amount4"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection12.section12Amount4}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection12.section12Amount4', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection12.section12Amount4', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection12.section12Amount4', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section12Code4"
                name="section12Code4"
                label=""
                value={formik.values.w2FormSection12.section12Code4}
                onChange={event => formik.setFieldValue('w2FormSection12.section12Code4', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <p style={{ margin: 0, fontSize: '16px' }}>13.</p>
              <CustomCheckBox
                label="Staturary Empolyee"
                id="statutoryEmployeeIndicator"
                name="statutoryEmployeeIndicator"
                checked={formik.values.w2Form.statutoryEmployeeIndicator}
                onChange={() => {
                  formik.setFieldValue(
                    'w2Form.statutoryEmployeeIndicator',
                    !formik.values.w2Form.statutoryEmployeeIndicator
                  );
                }}
              />
              <CustomCheckBox
                label="Retirement Plan"
                id="retirementPlanIndicator"
                name="retirementPlanIndicator"
                checked={formik.values.w2Form.retirementPlanIndicator}
                onChange={() => {
                  formik.setFieldValue('w2Form.retirementPlanIndicator', !formik.values.w2Form.retirementPlanIndicator);
                }}
              />
              <CustomCheckBox
                label="Third-party Sick Pay"
                id="thirdPartySickPayIndicator"
                name="thirdPartySickPayIndicator"
                checked={formik.values.w2Form.thirdPartySickPayIndicator}
                onChange={() => {
                  formik.setFieldValue(
                    'w2Form.thirdPartySickPayIndicator',
                    !formik.values.w2Form.thirdPartySickPayIndicator
                  );
                }}
              />
            </RowItem>
            <RowItem>{/* Another empty placeholder for the sixth item */}</RowItem>
          </Row>
          <Row>
            <RowItem>
              <CommonCurrencyInput
                id="section14Amount1"
                label={`14. ${t('w2.form.value.other')}`}
                name="section14Amount1"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection14.section14Amount1}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection14.section14Amount1', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection14.section14Amount1', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection14.section14Amount1', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section14Label1"
                name="section14Label1"
                label=""
                value={formik.values.w2FormSection14.section14Label1}
                onChange={event => formik.setFieldValue('w2FormSection14.section14Label1', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="section14Amount2"
                label=""
                name="section14Amount2"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection14.section14Amount2}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection14.section14Amount2', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection14.section14Amount2', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection14.section14Amount2', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section14Label2"
                name="section14Label2"
                label=""
                value={formik.values.w2FormSection14.section14Label2}
                onChange={event => formik.setFieldValue('w2FormSection14.section14Label2', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="section14Amount3"
                label=""
                name="section14Amount3"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection14.section14Amount3}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection14.section14Amount3', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection14.section14Amount3', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection14.section14Amount3', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section14Label3"
                name="section14Label3"
                label=""
                value={formik.values.w2FormSection14.section14Label3}
                onChange={event => formik.setFieldValue('w2FormSection14.section14Label3', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="section14Amount4"
                label=""
                name="section14Amount4"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection14.section14Amount4}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection14.section14Amount4', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection14.section14Amount4', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection14.section14Amount4', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section14Label4"
                name="section14Label4"
                label=""
                value={formik.values.w2FormSection14.section14Label4}
                onChange={event => formik.setFieldValue('w2FormSection14.section14Label4', event.target.value)}
              />
            </RowItem>
            <RowItem>
              <CommonCurrencyInput
                id="section14Amount5"
                label=""
                name="section14Amount5"
                placeholder="Please enter a number"
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                decimalsLimit={6}
                allowNegativeValue={false}
                value={formik.values.w2FormSection14.section14Amount5}
                onValueChange={v => {
                  if (v === undefined) {
                    formik.setFieldValue('w2FormSection14.section14Amount5', 0);
                  } else {
                    const numericValue = v;
                    if (!Number.isNaN(numericValue)) {
                      formik.setFieldValue('w2FormSection14.section14Amount5', numericValue);
                    } else {
                      formik.setFieldValue('w2FormSection14.section14Amount5', 0);
                    }
                  }
                }}
              />
              <CommonInput
                id="section14Label5"
                name="section14Label5"
                label=""
                value={formik.values.w2FormSection14.section14Label5}
                onChange={event => formik.setFieldValue('w2FormSection14.section14Label5', event.target.value)}
              />
            </RowItem>
            <RowItem>{/* Another empty placeholder for the sixth item */}</RowItem>
          </Row>

          {employeeData?.data.w2FormTaxes.map((tax: any, index: any) => (
            <Rowlast>
              <RowItem>
                <CommonInput
                  id={`state1_${index}`}
                  name={`state1_${index}`}
                  label={index === 0 ? 'State Employer ID Number' : ''}
                  value={tax.state1}
                  onChange={event => {
                    const updatedW2FormTaxes = [...formik.values.w2FormTaxes];
                    updatedW2FormTaxes[index].state1 = event.target.value;
                    formik.setFieldValue('w2FormTaxes', updatedW2FormTaxes);
                  }}
                />
              </RowItem>
              <RowItem>
                <CommonCurrencyInput
                  id={`stateIncomeTax1_${index}`}
                  name={`stateIncomeTax1_${index}`}
                  label={index === 0 ? 'State Wages, Tips, etc' : ''}
                  value={tax.stateWages1}
                  prefix="$"
                  decimalsLimit={6}
                  onValueChange={v => {
                    let numericValue: string | number | undefined = v;
                    if (Number.isNaN(numericValue)) {
                      numericValue = 0;
                    }
                    const updatedW2FormTaxes = [...formik.values.w2FormTaxes];
                    updatedW2FormTaxes[index].stateWages1 = numericValue;
                    formik.setFieldValue('w2FormTaxes', updatedW2FormTaxes);
                  }}
                />
              </RowItem>
              <RowItem>
                <CommonCurrencyInput
                  id={`stateIncomeTax1_${index}`}
                  name={`stateIncomeTax1_${index}`}
                  label={index === 0 ? 'State income tax' : ''}
                  value={tax.stateIncomeTax1}
                  prefix="$"
                  groupSeparator=","
                  onValueChange={v => {
                    let numericValue: string | number | undefined = v;
                    if (Number.isNaN(numericValue)) {
                      numericValue = 0;
                    }
                    const updatedW2FormTaxes = [...formik.values.w2FormTaxes];
                    updatedW2FormTaxes[index].stateIncomeTax1 = numericValue;
                    formik.setFieldValue('w2FormTaxes', updatedW2FormTaxes);
                  }}
                />
              </RowItem>
              <RowItem>
                <CommonCurrencyInput
                  id={`stateWages1_${index}`}
                  name={`stateWages1_${index}`}
                  label={index === 0 ? 'Local Wages, Tips, etc' : ''}
                  prefix="$"
                  decimalSeparator="."
                  value={tax.formLocal ? tax.formLocal.localWages1 : ''}
                  onValueChange={v => {
                    let numericValue: string | number | undefined = v;
                    if (Number.isNaN(numericValue)) {
                      numericValue = 0;
                    }
                    const updatedW2FormTaxes = [...formik.values.w2FormTaxes];
                    if (updatedW2FormTaxes[index].formLocal) {
                      updatedW2FormTaxes[index].formLocal.localWages1 = numericValue;
                    } else {
                      updatedW2FormTaxes[index].formLocal = {
                        localWages1: numericValue,
                      };
                    }
                    formik.setFieldValue('w2FormTaxes', updatedW2FormTaxes);
                  }}
                />
              </RowItem>
              <RowItem>
                <CommonCurrencyInput
                  id={`stateWages1_${index}`}
                  name={`stateWages1_${index}`}
                  label={index === 0 ? 'Local Income tax' : ''}
                  prefix="$"
                  allowNegativeValue={false}
                  value={tax.formLocal ? tax.formLocal.localIncomeTax1 : ''}
                  onValueChange={v => {
                    let numericValue: string | number | undefined = v;
                    if (Number.isNaN(numericValue)) {
                      numericValue = 0;
                    }
                    const updatedW2FormTaxes = [...formik.values.w2FormTaxes];
                    if (updatedW2FormTaxes[index].formLocal) {
                      updatedW2FormTaxes[index].formLocal.localIncomeTax1 = numericValue;
                    } else {
                      updatedW2FormTaxes[index].formLocal = {
                        localIncomeTax1: numericValue,
                      };
                    }
                    formik.setFieldValue('w2FormTaxes', updatedW2FormTaxes);
                  }}
                />
              </RowItem>
              <RowItem>
                <CommonInput
                  id={`state1_${index}`}
                  name={`state1_${index}`}
                  label={index === 0 ? 'Locality name' : ''}
                  value={tax.formLocal ? tax.formLocal.localityName1 : ''}
                  onChange={event => {
                    const updatedW2FormTaxes = [...formik.values.w2FormTaxes];
                    if (updatedW2FormTaxes[index].formLocal) {
                      updatedW2FormTaxes[index].formLocal.localityName1 = event.target.value;
                    } else {
                      updatedW2FormTaxes[index].formLocal = {
                        localityName1: event.target.value,
                      };
                    }
                    formik.setFieldValue('w2FormTaxes', updatedW2FormTaxes);
                  }}
                />
              </RowItem>
            </Rowlast>
          ))}
        </>
      )}
    </Container>
  );
};
